import { useRef, useState, useEffect, useMemo, useCallback } from "react";
import {
	extractSignedParams,
	extractFilenameFromUrl,
	isHlsManifestUrl,
	HLS_MAX_RETRIES,
	HLS_LOADING_TIMEOUT_MS,
} from "./audioVideoUtils";

interface IUseHlsPlaybackParams {
	mediaUrl: string;
	presignedSegmentUrls?: string[];
	onReady?: () => void;
	onError?: () => void;
}

interface IUseHlsPlaybackReturn {
	mediaRef: React.RefObject<HTMLMediaElement>;
	isLoading: boolean;
	hasError: boolean;
	handleLoadedMetadataOrData: () => void;
	handleNativeError: () => void;
}

function useRefState<T>(initial: T): [T, React.Dispatch<React.SetStateAction<T>>, React.MutableRefObject<T>] {
	const [state, setState] = useState(initial);
	const ref = useRef(initial);
	ref.current = state;
	return [state, setState, ref];
}

export const useHlsPlayback = ({
	mediaUrl,
	presignedSegmentUrls,
	onReady,
	onError,
}: IUseHlsPlaybackParams): IUseHlsPlaybackReturn => {
	const mediaRef = useRef<HTMLMediaElement>(null);
	const hlsRef = useRef<any>(null);
	const isMountedRef = useRef(true);
	const hlsRetryCountRef = useRef(0);
	const hasFallenBackRef = useRef(false);
	const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const hasReceivedFragRef = useRef(false);
	const blobUrlRef = useRef<string>("");

	const [isLoading, setIsLoading, isLoadingRef] = useRefState(true);
const [hasError, setHasError, hasErrorRef] = useRefState(false);
const [activeUrl, setActiveUrl] = useState(mediaUrl);

const onReadyRef = useRef(onReady);
onReadyRef.current = onReady;
const onErrorRef = useRef(onError);
onErrorRef.current = onError;

	const segmentUrlMap = useMemo(() => {
		const map = new Map<string, string>();
		if (!presignedSegmentUrls) return map;
		presignedSegmentUrls.forEach((url) => {
			try {
				const pathname = new URL(url).pathname;
				const filename = pathname.split("/").pop() || "";
				if (filename) {
					map.set(filename, url);
				}
			} catch {
				// skip malformed
			}
		});
		return map;
	}, [presignedSegmentUrls]);

	const presignedM3u8Url = useMemo(() => {
		if (!presignedSegmentUrls || presignedSegmentUrls.length === 0) return "";
		return (
			presignedSegmentUrls.find((u) => {
				try {
					return new URL(u).pathname.endsWith(".m3u8");
				} catch {
					return u.includes(".m3u8");
				}
			}) || ""
		);
	}, [presignedSegmentUrls]);

	const isHlsUrl = useMemo(() => isHlsManifestUrl(activeUrl), [activeUrl]);

	// Reset when mediaUrl prop changes
	useEffect(() => {
		hasFallenBackRef.current = false;
		hasReceivedFragRef.current = false;
		setActiveUrl(mediaUrl);
	}, [mediaUrl]);

	// Mount/unmount lifecycle — single source of truth
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
			if (loadingTimeoutRef.current) {
				clearTimeout(loadingTimeoutRef.current);
				loadingTimeoutRef.current = null;
			}
			if (hlsRef.current) {
				hlsRef.current.destroy();
				hlsRef.current = null;
			}
			if (mediaRef.current) {
				mediaRef.current.pause();
				mediaRef.current.removeAttribute("src");
				mediaRef.current.load();
			}
			if (blobUrlRef.current) {
				URL.revokeObjectURL(blobUrlRef.current);
				blobUrlRef.current = "";
			}
		};
	}, []);

	// Main playback setup
	useEffect(() => {
		hasReceivedFragRef.current = false;
		const media = mediaRef.current;
		if (!media || !activeUrl) return;

		hlsRetryCountRef.current = 0;

		if (loadingTimeoutRef.current) {
			clearTimeout(loadingTimeoutRef.current);
			loadingTimeoutRef.current = null;
		}

		if (hlsRef.current) {
			hlsRef.current.destroy();
			hlsRef.current = null;
		}

		if (blobUrlRef.current) {
			URL.revokeObjectURL(blobUrlRef.current);
			blobUrlRef.current = "";
		}

		let cancelled = false;
		let fetchController: AbortController | null = null;

		const doFallback = () => {
			if (loadingTimeoutRef.current) {
				clearTimeout(loadingTimeoutRef.current);
				loadingTimeoutRef.current = null;
			}
			if (hlsRef.current) {
				hlsRef.current.destroy();
				hlsRef.current = null;
			}
			if (!hasFallenBackRef.current && presignedM3u8Url) {
				hasFallenBackRef.current = true;
				hlsRetryCountRef.current = 0;
				hasReceivedFragRef.current = false;
				setIsLoading(true);
				setHasError(false);
				setActiveUrl(presignedM3u8Url);
			} else {
				setHasError(true);
				setIsLoading(false);
				onErrorRef.current?.();
			}
		};

		const setupPlayback = async () => {
			if (isHlsUrl) {
				// @ts-ignore
				const { default: Hls } = await import("hls.js/dist/hls.light.min.js");

				if (!isMountedRef.current || cancelled) return;

				if (!Hls.isSupported()) {
					if (media.canPlayType("application/vnd.apple.mpegurl")) {
						setIsLoading(true);
						setHasError(false);
						media.src = activeUrl;
						media.load();
						return;
					}
					setHasError(true);
					setIsLoading(false);
					onErrorRef.current?.();
					return;
				}

				setIsLoading(true);
				setHasError(false);

				const hls = new Hls({
					xhrSetup: hasFallenBackRef.current
						? (xhr: XMLHttpRequest, url: string) => {
								const filename = extractFilenameFromUrl(url);
								const presigned = segmentUrlMap.get(filename);
								if (presigned) {
									xhr.open("GET", presigned, true);
									return;
								}
							}
						: (xhr: XMLHttpRequest, url: string) => {
								const signParams = extractSignedParams(activeUrl);
								if (!signParams) return;
								if (
									url.includes("Policy=") ||
									url.includes("X-Amz-Signature=") ||
									url.includes("AWSAccessKeyId=")
								)
									return;
								const separator = url.includes("?") ? "&" : "?";
								xhr.open("GET", `${url}${separator}${signParams}`, true);
							},
				});
				hlsRef.current = hls;

				hls.on(Hls.Events.MANIFEST_PARSED, (_event: any, data: any) => {
					if (!isMountedRef.current) return;
					if (!data.levels || data.levels.length === 0) {
						hls.destroy();
						hlsRef.current = null;
						doFallback();
						return;
					}
					if (loadingTimeoutRef.current) {
						clearTimeout(loadingTimeoutRef.current);
						loadingTimeoutRef.current = setTimeout(() => {
							if (!isMountedRef.current || hasReceivedFragRef.current || hasErrorRef.current) return;
							if (hlsRef.current) {
								hlsRef.current.destroy();
								hlsRef.current = null;
							}
							doFallback();
						}, HLS_LOADING_TIMEOUT_MS);
					}
				});

				hls.on(Hls.Events.FRAG_LOADED, () => {
					if (!isMountedRef.current) return;
					if (hasReceivedFragRef.current) return;
					hasReceivedFragRef.current = true;

					if (loadingTimeoutRef.current) {
						clearTimeout(loadingTimeoutRef.current);
						loadingTimeoutRef.current = null;
					}

					hlsRetryCountRef.current = 0;
					setIsLoading(false);
					setHasError(false);
					onReadyRef.current?.();
				});

				hls.on(Hls.Events.ERROR, (_event: any, data: any) => {
					if (!isMountedRef.current) return;

					if (!data.fatal && data.details === "fragLoadError" && !hasReceivedFragRef.current) {
						doFallback();
						return;
					}

					if (data.fatal) {
						switch (data.type) {
							case Hls.ErrorTypes.NETWORK_ERROR:
								if (
									data.details === "manifestLoadError" ||
									data.details === "manifestParsingError"
								) {
									doFallback();
								} else if (hlsRetryCountRef.current < HLS_MAX_RETRIES) {
									hlsRetryCountRef.current += 1;
									hls.startLoad();
								} else {
									doFallback();
								}
								break;
							case Hls.ErrorTypes.MEDIA_ERROR:
								hls.recoverMediaError();
								break;
							default:
								doFallback();
								break;
						}
					}
				});

				hls.loadSource(activeUrl);
				hls.attachMedia(media);

				loadingTimeoutRef.current = setTimeout(() => {
					if (!isMountedRef.current || hasReceivedFragRef.current || hasErrorRef.current) return;
					if (hlsRef.current) {
						hlsRef.current.destroy();
						hlsRef.current = null;
					}
					doFallback();
				}, HLS_LOADING_TIMEOUT_MS);
			} else {
				// Non-HLS
				setIsLoading(true);
				setHasError(false);

				const isPresignedUrl = (() => {
					try {
						const url = new URL(activeUrl);
						return (
							url.searchParams.has("Policy") ||
							url.searchParams.has("Signature") ||
							url.searchParams.has("X-Amz-Signature") ||
							url.searchParams.has("X-Amz-Credential")
						);
					} catch {
						return false;
					}
				})();

				if (isPresignedUrl) {
					media.src = activeUrl;
					media.load();
					setIsLoading(false);
				} else {
					fetchController = new AbortController();
					fetch(activeUrl, { signal: fetchController.signal })
						.then((response) => {
							if (!response.ok) throw new Error(`HTTP ${response.status}`);
							const contentType = response.headers.get("content-type") || "";
							if (!contentType.startsWith("audio/") && !contentType.startsWith("video/")) {
								throw new Error(`Unexpected content type: ${contentType}`);
							}
							return response.blob();
						})
						.then((blob) => {
							if (cancelled || !isMountedRef.current) return;
							const url = URL.createObjectURL(blob);
							blobUrlRef.current = url;
							media.src = url;
							media.load();
							setIsLoading(false);
						})
						.catch((err) => {
							if (err.name === "AbortError") return;
							console.error("[HlsPlayback] Blob fetch error:", err);
							if (!cancelled && isMountedRef.current) {
								setHasError(true);
								setIsLoading(false);
								onErrorRef.current?.();
							}
						});
				}
			}
		};

		setupPlayback();

		return () => {
			cancelled = true;
			if (loadingTimeoutRef.current) {
				clearTimeout(loadingTimeoutRef.current);
				loadingTimeoutRef.current = null;
			}
			if (hlsRef.current) {
				hlsRef.current.destroy();
				hlsRef.current = null;
			}
			if (fetchController) {
				fetchController.abort();
			}
			if (media) {
				media.pause();
				if (!isHlsUrl) {
					media.removeAttribute("src");
					media.load();
				}
			}
			if (blobUrlRef.current) {
				URL.revokeObjectURL(blobUrlRef.current);
				blobUrlRef.current = "";
			}
		};
		// onReady/onError intentionally excluded: they are captured via onReadyRef/onErrorRef so
		// parent callback identity changes don't tear down HLS playback. See review item A5.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeUrl, isHlsUrl, segmentUrlMap, presignedM3u8Url]);

	// For native HLS (Safari) or direct src — called from onLoadedMetadata / onLoadedData
	const handleLoadedMetadataOrData = useCallback(() => {
		if (!isMountedRef.current) return;
		if (!hlsRef.current) {
			if (loadingTimeoutRef.current) {
				clearTimeout(loadingTimeoutRef.current);
				loadingTimeoutRef.current = null;
			}
			setIsLoading(false);
			setHasError(false);
			onReadyRef.current?.();
		}
	}, [onReady]);

	// For native <audio>/<video> error when HLS is NOT managing playback
	const handleNativeError = useCallback(() => {
		if (!isMountedRef.current) return;
		if (hlsRef.current) return;

		if (isHlsUrl) {
			if (!hasFallenBackRef.current && presignedM3u8Url) {
				hasFallenBackRef.current = true;
				hasReceivedFragRef.current = false;
				setIsLoading(true);
				setHasError(false);
				setActiveUrl(presignedM3u8Url);
			} else {
				setHasError(true);
				setIsLoading(false);
				onErrorRef.current?.();
			}
			return;
		}

		setHasError(true);
		setIsLoading(false);
		onErrorRef.current?.();
	}, [onError, isHlsUrl, presignedM3u8Url]);

	return {
		mediaRef,
		isLoading,
		hasError,
		handleLoadedMetadataOrData,
		handleNativeError,
	};
};